import { Skeleton } from '@factorialco/deprecated-design-system'
import { type SelectOption } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Select from 'modules/core/components/Inputs/Select'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { useFilters } from 'modules/shift_management/hooks/useFilters'
import { usePermissions } from 'modules/shift_management/hooks/usePermissions'
import type UnassignedShiftForm from 'modules/shift_management/lib/forms/UnassignedShiftForm'
import { getSelectableTeams } from 'modules/shift_management/lib/getSelectableTeams'
import { useSelectableLocations } from 'modules/shift_management/lib/useSelectableLocations'
import allTeams from 'modules/teams/store/collections/teams'

interface Props {
  form: UnassignedShiftForm
}

const ShiftAccessSelectors = ({ form }: Props) => {
  const [{ canCreateUnassignedShiftsForEverybody }] = usePermissions()
  const { employee: currentEmployee } = useCurrentSession()
  const teamField = form.get('teams_ids_visibilities')
  const locationField = form.get('locations_ids_visibilities')
  const { loading, locations } = useSelectableLocations(
    currentEmployee?.id ?? 0,
    !!canCreateUnassignedShiftsForEverybody
  )
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const newEntryFlowEnabled = hasFeatureFlag('dev_shift_management_autoscheduler_entry_flow')
  const [{ locationIds }] = useFilters()
  const currentLocation = locationIds.at(0)

  const selectableTeams = currentEmployee
    ? getSelectableTeams(currentEmployee.id, canCreateUnassignedShiftsForEverybody)
    : allTeams

  const teamOptions = useMemo<SelectOption[]>(() => {
    const options = (selectableTeams || []).map((team) => ({
      label: team.attributes.name,
      value: team.attributes.id,
    }))
    const sortedTeams = options.sort((a, b) => a.label.localeCompare(b.label))

    return sortedTeams
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const locationOptions = locations.map((location) => ({
    label: location.name,
    value: location.id,
  }))

  useUnsafeEffect(() => {
    if (locations.length === 0 || locationField.value) return

    if (newEntryFlowEnabled) {
      locationField.set(String(currentLocation))
      return
    }

    const currentLocationOption = locations.find(
      (loc) => Number(loc.id) === Number(currentLocation)
    )
    const selectedLocation = currentLocationOption?.id || ''
    locationField.set(String(selectedLocation))
  }, [locations.length])

  const onChangeTeam = (team: string) => {
    teamField.set(team)
  }

  const onChangeLocation = (workplace: string) => {
    locationField.set(workplace)
  }

  return (
    <>
      <Select
        none={i18n.t('shift_management.coverage_section.access.selector_all_company')}
        options={teamOptions}
        field={teamField}
        onChange={(team) => onChangeTeam(team.value)}
      />
      {loading ? (
        <Skeleton />
      ) : (
        <Select
          none={i18n.t('shift_management.coverage_section.access.selector_team_or_workplace')}
          options={locationOptions}
          field={locationField}
          onChange={(workplace) => onChangeLocation(workplace.value)}
          defaultValue={locationField.value}
          mandatory
          hideLabel
          label={i18n.t('shift_management.coverage_section.access.selector_team_or_workplace')}
        />
      )}
    </>
  )
}

export default observer(ShiftAccessSelectors)
